"use client";

import React from "react";
import { Clock, Coins, TrendingUp, Wrench } from "lucide-react";
import { Area, AreaChart, CartesianGrid, XAxis } from "recharts";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const chartData = [{
  month: "Jan",
  traffic: 100,
  revenue: 0
}, {
  month: "Feb",
  traffic: 120,
  revenue: 40
}, {
  month: "Mar",
  traffic: 170,
  revenue: 110
}, {
  month: "Apr",
  traffic: 250,
  revenue: 220
}, {
  month: "May",
  traffic: 380,
  revenue: 390
}, {
  month: "Jun",
  traffic: 550,
  revenue: 650
}, {
  month: "Jul",
  traffic: 700,
  revenue: 900
}];
const chartConfig = {
  traffic: {
    label: "Foot Traffic Increase",
    color: "var(--chart-2)"
  },
  revenue: {
    label: "Extra Revenue (€)",
    color: "var(--chart-1)"
  }
} satisfies ChartConfig;
const valueProps = [{
  title: "Increased Stay Time",
  description: "Customers stay longer when they don't have to rush home to charge their phones. Longer stays naturally translate to increased orders and higher average spend per visit.",
  icon: Clock
}, {
  title: "Zero Maintenance",
  description: "We handle absolutely everything. From the initial installation to ongoing maintenance and 24/7 customer support, you enjoy the benefits without lifting a finger.",
  icon: Wrench
}, {
  title: "Additional Income",
  description: "Turn a small footprint into a passive income stream. You earn a share of the rental revenue generated from the ZapGO! station hosted at your venue.",
  icon: Coins
}];
export default function IncreaseYourRevenue() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="verhoog-uw-omzet" className="bg-background relative overflow-hidden py-24">
      {}
      <div className="container relative z-10 mx-auto mb-12 px-4 text-center">
        <h2 className="text-foreground mb-4 text-3xl font-bold tracking-tight md:text-5xl">
          Transform Foot Traffic into Revenue
        </h2>
        <p className="text-muted-foreground mx-auto max-w-2xl text-lg">
          Partnering with ZapGO! isn't just about offering a service; it's about upgrading your venue's experience and boosting your bottom line with zero effort.
        </p>
      </div>

      {}
      <div className="relative z-0 h-[400px] w-full opacity-80 md:h-[500px] dark:opacity-60">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-background/90 z-10 pointer-events-none" />
        <ChartContainer config={chartConfig} className="h-full w-full">
          <AreaChart data={chartData} margin={{
          top: 0,
          right: 0,
          left: 0,
          bottom: 0
        }}>
            <defs>
              <linearGradient id="fillTraffic" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-traffic)" stopOpacity={0.4} />
                <stop offset="95%" stopColor="var(--color-traffic)" stopOpacity={0.0} />
              </linearGradient>
              <linearGradient id="fillRevenue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-revenue)" stopOpacity={0.4} />
                <stop offset="95%" stopColor="var(--color-revenue)" stopOpacity={0.0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" opacity={0.3} />
            <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} tick={{
            fill: 'var(--muted-foreground)'
          }} />
            <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
            <Area type="monotone" dataKey="traffic" stroke="var(--color-traffic)" fill="url(#fillTraffic)" strokeWidth={3} />
            <Area type="monotone" dataKey="revenue" stroke="var(--color-revenue)" fill="url(#fillRevenue)" strokeWidth={3} />
          </AreaChart>
        </ChartContainer>
      </div>

      {}
      <div className="container relative z-20 mx-auto -mt-32 px-4 md:-mt-48">
        <div ref={ref} className={`grid grid-cols-1 gap-6 transition-all duration-1000 md:grid-cols-3 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
          {valueProps.map((prop, i) => <Card key={i} data-index={i} className="border-border/50 bg-card/90 shadow-xl backdrop-blur-md transition-shadow duration-300 hover:shadow-2xl">
              <CardHeader>
                <div className="bg-primary/10 mb-4 flex size-12 items-center justify-center rounded-xl">
                  <prop.icon className="text-primary size-6" />
                </div>
                <CardTitle className="text-xl">{prop.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground leading-relaxed">
                  {prop.description}
                </p>
              </CardContent>
            </Card>)}
        </div>
      </div>

      {}
      <div className="container relative z-20 mx-auto mt-20 px-4 text-center">
        <div className="flex flex-col items-center justify-center space-y-6">
          <h3 className="text-foreground text-2xl font-semibold md:text-3xl">
            Ready to boost your venue's appeal?
          </h3>
          <Button asChild size="lg" className="rounded-full px-8 py-6 text-lg">
            <Link to="/PartnersPage">
              Join the ZapGO! Network <TrendingUp className="ml-2 size-5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}